import MobileBackButton from "./MobileBackButton";
import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  FiCheck,
  FiChevronDown,
  FiChevronRight,
  FiSearch,
  FiX,
} from "react-icons/fi";
import { FaCalendarDays, FaCaretDown } from "react-icons/fa6";

import noTeamImg from "../../assets/no-team.png";
import "./AttendanceRecords.css";

/*
 * Presentational Attendance Log & Team Attendance screens shared by the
 * user & admin mobile pages. All data / behaviour stays in the page; this
 * only renders it. The page binds its daterangepicker to `pickerRef` and
 * should add the "mobile-daterangepicker" class to the picker container.
 */

// "Sep" -> "Sept", as on the attendance screen
const monthOf = (m) => (m.format("MMM") === "Sep" ? "Sept" : m.format("MMM"));

const formatRangeDate = (date) => {
  const m = moment(date);
  return `${m.format("DD")} ${monthOf(m)} ${m.format("YYYY")}`;
};

const formatGroupDate = (date) => {
  const m = moment(date);
  return `${m.format("ddd, DD")} ${monthOf(m)} ${m.format("YYYY")}`;
};

const formatPunch = (dateTime) =>
  dateTime ? moment(dateTime).format("h:mm A") : "-";

// "Week Off" / "Weekoff" -> "weekoff", "Early Out" -> "earlyout"
const statusKey = (type) =>
  String(type || "").toLowerCase().replace(/[\s_-]+/g, "");

const initialsOf = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "-";

const StatusBadge = ({ type }) => (
  <span className={`ar-status ar-status-${statusKey(type)}`}>
    {type || "-"}
  </span>
);

const TitleRow = ({ title, onBack }) => (
  <div className="ar-title-row">
    <MobileBackButton type="button" className="ar-back" onClick={onBack} aria-label="Back" />
    <h2 className="ar-title">{title}</h2>
  </div>
);

const RangeFilter = ({ pickerRef, startDate, endDate }) => (
  <button type="button" ref={pickerRef} className="ar-card ar-range">
    <span className="ar-range-icon">
      <FaCalendarDays />
    </span>
    <span className="ar-range-text">
      <span className="ar-range-label">Date range</span>
      <span className="ar-range-value">
        {formatRangeDate(startDate)} - {formatRangeDate(endDate)}
      </span>
    </span>
    <FaCaretDown className="ar-range-caret" />
  </button>
);


/* =====================================================
   ATTENDANCE LOG (my records)
===================================================== */

// "September 2026" for one month, "Sep - Oct 2026" / "Dec 2025 - Jan 2026" across months
const formatPeriodTitle = (startDate, endDate) => {
  const from = moment(startDate);
  const to = moment(endDate);

  if (from.isSame(to, "month")) return from.format("MMMM YYYY");
  if (from.isSame(to, "year")) return `${from.format("MMM")} - ${to.format("MMM YYYY")}`;
  return `${from.format("MMM YYYY")} - ${to.format("MMM YYYY")}`;
};

const formatPeriodRange = (startDate, endDate) =>
  `${moment(startDate).format("DD MMM")} - ${moment(endDate).format("DD MMM")}`;

const formatPunchRange = (item) => {
  if (!item.first_punch && !item.last_punch) return "No Punches recorded";

  const fmt = (dateTime) => (dateTime ? moment(dateTime).format("HH:mm") : "--:--");
  return `${fmt(item.first_punch)} - ${fmt(item.last_punch)}`;
};

const PeriodFilter = ({ pickerRef, startDate, endDate }) => (
  <button type="button" ref={pickerRef} className="ar-card ar-range ar-range-month">
    <FaCalendarDays className="ar-month-icon" />
    <span className="ar-range-text">
      <span className="ar-month-title">{formatPeriodTitle(startDate, endDate)}</span>
      <span className="ar-month-range">{formatPeriodRange(startDate, endDate)}</span>
    </span>
    <span className="ar-month-caret">
      <FiChevronDown />
    </span>
  </button>
);

export const AttendanceLogView = ({
  pickerRef,
  startDate,
  endDate,
  records,
  isRegularizeDisabled,
  onRegularize,
  onBack,
}) => (
  <div className="ar-page">
    <TitleRow title="Attendance History" onBack={onBack} />

    <PeriodFilter pickerRef={pickerRef} startDate={startDate} endDate={endDate} />

    <hr className="ar-separator" />

    <div className="ar-list">
      {records.length === 0 ? (
        <div className="ar-card ar-empty">No attendance records found</div>
      ) : (
        records.map((item, index) => {
          const day = moment(item.date);

          return (
            <article key={`${item.date}-${index}`} className="ar-card ar-day">
              <div className="ar-date-box">
                <span className="ar-date-day">{day.format("DD")}</span>
                <span className="ar-date-week">{day.format("ddd")}</span>
              </div>

              <div className="ar-day-main">
                <span className={`ar-day-status ar-status-${statusKey(item.type)}`}>
                  {item.type || "-"}
                </span>
                <span className="ar-day-times">{formatPunchRange(item)}</span>
              </div>

              {!isRegularizeDisabled(item) && (
                <button
                  type="button"
                  className="ar-regularize"
                  onClick={() => onRegularize(item)}
                >
                  Regularize <FiChevronRight />
                </button>
              )}
            </article>
          );
        })
      )}
    </div>
  </div>
);


/* =====================================================
   TEAM ATTENDANCE
===================================================== */

export const TeamAttendanceView = ({
  pickerRef,
  startDate,
  endDate,
  members,
  membersStatus = "ready",
  selectedUserIds,
  onToggleMember,
  onRemoveMember,
  onClearSelection,
  records,
  onBack,
}) => {
  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  // close the dropdown on an outside tap
  useEffect(() => {
    if (!showDropdown) return undefined;

    const handleOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, [showDropdown]);

  // the app-wide loader covers the page while team members load
  const loadingMembers = membersStatus === "loading";
  const noReports = membersStatus === "ready" && members.length === 0;
  const memberMessage = membersStatus === "loading"
    ? "Loading team members..."
    : membersStatus === "error"
      ? "Unable to load team members. Please try again."
      : noReports
        ? "No one reporting to you"
        : null;

  const q = query.trim().toLowerCase();
  const filteredMembers = q
    ? members.filter((m) =>
        [m.name, m.department, m.employee_code].some((value) =>
          String(value || "").toLowerCase().includes(q),
        ),
      )
    : members;

  const selectedMembers = members.filter((m) => selectedUserIds.includes(m.id));

  const memberById = new Map(members.map((m) => [m.id, m]));

  // records arrive sorted by date: one card per date
  const groups = [];
  records.forEach((row) => {
    const last = groups[groups.length - 1];
    if (last && last.date === row.date) last.rows.push(row);
    else groups.push({ date: row.date, rows: [row] });
  });

  const handleClear = () => {
    setQuery("");
    onClearSelection();
    setShowDropdown(true);
    inputRef.current?.focus();
  };

  return (
    <div className="ar-page">
      <TitleRow title="Team Attendance" onBack={onBack} />

      {/* hidden, not unmounted, so the page's daterangepicker stays bound */}
      <div className={`ar-filters ${loadingMembers || noReports ? "ar-hidden" : ""}`}>
        <RangeFilter pickerRef={pickerRef} startDate={startDate} endDate={endDate} />

        <div className="ar-search" ref={searchRef}>
          <div
            className="ar-card ar-search-box"
            onClick={() => {
              setShowDropdown(true);
              inputRef.current?.focus();
            }}
          >
            <FiSearch className="ar-search-icon" />

            <div className="ar-search-field">
              {selectedMembers.map((member) => (
                <span key={member.id} className="ar-chip">
                  {member.name}
                  <button
                    type="button"
                    className="ar-chip-remove"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveMember(member.id);
                    }}
                    aria-label={`Remove ${member.name}`}
                  >
                    <FiX />
                  </button>
                </span>
              ))}

              <input
                ref={inputRef}
                type="text"
                className="ar-search-input"
                placeholder={selectedMembers.length > 0 ? "" : "Search member..."}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
              />
            </div>

            {(query || selectedUserIds.length > 0) && (
              <button
                type="button"
                className="ar-search-clear"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                aria-label="Clear search"
              >
                <FiX />
              </button>
            )}
          </div>

          {showDropdown && (
            <div className="ar-dropdown">
              {filteredMembers.length === 0 ? (
                <div className="ar-dropdown-empty">{memberMessage || "No users found"}</div>
              ) : (
                filteredMembers.map((member) => {
                  const selected = selectedUserIds.includes(member.id);

                  return (
                    <button
                      type="button"
                      key={member.id}
                      className={`ar-dropdown-item ${selected ? "selected" : ""}`}
                      onClick={() => {
                        onToggleMember(member);
                        setQuery("");
                      }}
                    >
                      <span className="ar-avatar">{initialsOf(member.name)}</span>
                      <span className="ar-member-main">
                        <span className="ar-member-name">{member.name}</span>
                        {member.department && (
                          <span className="ar-member-sub">{member.department}</span>
                        )}
                      </span>
                      {selected && <FiCheck className="ar-dropdown-check" />}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {loadingMembers ? null : noReports ? (
        <div className="ar-no-team">
          <img src={noTeamImg} alt="" className="ar-no-team-img" />

          <div className="ar-card ar-no-team-card" role="status">
            <h3 className="ar-no-team-title">No Team Yet!</h3>
            <p className="ar-no-team-text">
              Your team attendance will show up here once you're assigned to a team.
            </p>
          </div>
        </div>
      ) : (
        <div className="ar-list">
          {memberMessage ? (
            <div className="ar-card ar-empty" role="status">{memberMessage}</div>
          ) : groups.length === 0 ? (
            <div className="ar-card ar-empty">
              No attendance records found for the selected filters
            </div>
          ) : (
            groups.map((group) => (
              <section key={group.date} className="ar-card ar-group">
                <div className="ar-group-head">
                  <span className="ar-group-date">{formatGroupDate(group.date)}</span>
                  <span className="ar-group-count">
                    {group.rows.length} {group.rows.length === 1 ? "member" : "members"}
                  </span>
                </div>

                {group.rows.map((item, index) => {
                  const name =
                    item.user_name || memberById.get(item.user_id)?.name || "-";

                  return (
                    <div key={`${item.user_id}-${index}`} className="ar-member">
                      <span className="ar-avatar">{initialsOf(name)}</span>

                      <div className="ar-member-main">
                        <span className="ar-member-name">{name}</span>
                        <span className="ar-member-sub">
                          In <strong>{formatPunch(item.first_punch)}</strong>
                          {"  ·  "}
                          Out <strong>{formatPunch(item.last_punch)}</strong>
                        </span>
                      </div>

                      <StatusBadge type={item.type} />
                    </div>
                  );
                })}
              </section>
            ))
          )}
        </div>
      )}
    </div>
  );
};
